import roleService, { ROLES } from "../service/RoleService";

const normalizeText = (value = "") =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

const uniqueTerms = (...groups) => {
    const seen = new Set();
    const terms = [];

    groups.flat().forEach((term) => {
        const normalized = normalizeText(term);
        if (!normalized || seen.has(normalized)) {
            return;
        }
        seen.add(normalized);
        terms.push(normalized);
    });

    return terms;
};

const buildImplicitKeywords = ({ name, path, category, keywords = [] }) =>
    uniqueTerms(
        name,
        category,
        path
            .replace(/[/?=&_-]+/g, " ")
            .split(" ")
            .filter(Boolean),
        keywords,
    );

const createScreen = ({
    id,
    name,
    path,
    category,
    keywords = [],
    visibilityKey = "authenticated",
}) => ({
    id,
    name,
    path,
    category,
    visibilityKey,
    keywords: buildImplicitKeywords({ name, path, category, keywords }),
});

const buildStaticScreens = (category, screens, visibilityKey = "authenticated") =>
    screens.map(([id, name, path, keywords = [], customVisibilityKey]) =>
        createScreen({
            id,
            name,
            path,
            category,
            keywords,
            visibilityKey: customVisibilityKey || visibilityKey,
        }),
    );

const buildTabScreens = ({ category, basePath, prefix, visibilityKey = "authenticated", tabs }) =>
    Object.entries(tabs).map(([tab, spec]) =>
        createScreen({
            id: `${prefix}-${tab}`,
            name: spec.name,
            path: `${basePath}?tab=${tab}`,
            category,
            keywords: spec.keywords,
            visibilityKey: spec.visibilityKey || visibilityKey,
        }),
    );

const buildViewScreens = ({ category, basePath, prefix, visibilityKey = "authenticated", views }) =>
    Object.entries(views).map(([view, spec]) =>
        createScreen({
            id: `${prefix}-${view}`,
            name: spec.name,
            path: `${basePath}&view=${view}`,
            category,
            keywords: spec.keywords,
            visibilityKey: spec.visibilityKey || visibilityKey,
        }),
    );

const STATIC_SCREENS = [
    ...buildStaticScreens("General", [
        ["dashboard", "Dashboard", "/", ["home", "homepage", "main"]],
        ["profile", "My Profile", "/profile", ["account", "me", "user profile"]],
        ["calendar", "MT Calendar", "/calendar", ["events", "schedule"]],
        ["knowledgebase", "Knowledgebase", "/knowledgebase", ["documents", "files", "folders"]],
        ["knowledgebase-company-policies", "Company Policies", "/knowledgebase/company-policies", ["policies", "handbook", "leave policy", "password protection", "data protection", "business continuity", "credit card", "travel expense"]],
        ["knowledgebase-sustainability-cdp", "Sustainability & CDP", "/knowledgebase/sustainability-cdp", ["sustainability", "cdp", "climate", "carbon emissions", "environmental policy", "climate transition"]],
        ["analytics", "Analytics", "/analytics", ["insights", "metrics"]],
        ["qa", "Q&A", "/qa", ["questions", "answers"]],
        ["wfa", "MT Anywhere+", "/wfa", ["work from anywhere", "working from home", "remote request"]],
        ["surveys", "My Surveys", "/surveys", ["survey", "questionnaire", "feedback form", "enps"]],
    ]),
    ...buildStaticScreens("Company Directory", [
        ["company-about", "About Mediatropy", "/company-directory/about", ["company info"]],
        ["company-office", "Office Directory", "/company-directory/office", ["offices", "locations"]],
        ["company-team", "Team Directory", "/company-directory/team", ["teams", "staff directory"]],
        ["company-department", "Department Directory", "/company-directory/department", ["departments"]],
        ["company-organization", "Organization Chart", "/company-directory/organization", ["org chart", "organisation chart"]],
    ]),
    ...buildStaticScreens("Knowledge & AI", [
        ["wiki", "Wiki", "/wiki", ["wiki pages", "knowledge articles"]],
        ["wiki-tree", "Wiki Tree", "/wiki/tree", ["wiki hierarchy", "tree view"]],
        ["wiki-search", "Wiki Search", "/wiki/search", ["search wiki", "find wiki page"]],
        ["wiki-onboarding", "Wiki Onboarding", "/wiki/onboarding", ["onboarding hub"]],
        ["wiki-tags", "Wiki Tags", "/wiki/tags", ["wiki tag management"]],
        ["wiki-categories", "Wiki Categories", "/wiki/categories", ["wiki category management"]],
        ["wiki-templates", "Wiki Templates", "/wiki/templates", ["page templates"]],
        ["wiki-reviews", "Wiki Reviews", "/wiki/reviews", ["review queue"]],
        ["wiki-reports", "Wiki Reports", "/wiki/reports", ["wiki analytics"]],
        ["wiki-settings", "Wiki Settings", "/wiki/settings", ["wiki admin"]],
        ["wiki-new", "Create Wiki Page", "/wiki/new", ["new wiki page", "create article"]],
        ["ai-tools", "AI Tools", "/ai-tools", ["artificial intelligence", "automation tools"]],
        ["chatbot", "AI Chat", "/chatbot", ["chatbot", "assistant"]],
        ["chatbot-sections", "Chat Sections", "/chatbot/section", ["chat categories"]],
        ["chatbot-company-prompts", "Company Prompts", "/chatbot/company-prompt", ["company prompt library"]],
        ["chatbot-department-prompts", "Department Prompts", "/chatbot/department-prompt", ["department prompt library"]],
    ]),
    ...buildStaticScreens("Training", [
        ["training", "Learning & Development", "/training", ["learning", "courses", "lnd"]],
    ]),
    ...buildStaticScreens("Announcements", [
        ["newsfeed", "Newsfeed", "/newsfeed", ["feed", "linkedin", "company updates", "birthdays", "anniversaries", "shout outs"]],
        ["announcements", "Announcements", "/announcements", ["news", "bulletins"]],
        ["notifications", "Notifications", "/notification", ["alerts", "inbox"]],
        ["notifications-alias", "Notifications", "/notifications", ["alerts", "inbox"]],
        ["workplace-feedback", "Share Workplace Feedback with HR", "/workplace-feedback", ["workplace feedback", "feedback to hr", "anonymous feedback", "hr feedback"]],
    ]),
    ...buildStaticScreens("Admin", [
        ["admin-home", "Admin Home", "/admin", ["admin dashboard"], "admin_visible"],
        ["admin-activity-log", "Activity Log", "/admin/activity-log", ["audit log", "system activity"], "owner_or_switch_user"],
        ["admin-api-key-management", "API Key Management", "/admin/api-key-management", ["api keys", "tokens"], "owner_or_switch_user"],
        ["admin-api-usage", "API Usage Analysis", "/admin/api-usage", ["api analytics", "usage reports"], "owner_or_switch_user"],
        ["admin-aspire-invoice-allocation", "Aspire Invoice Allocation", "/admin/aspire-invoice-allocation", ["aspire", "card charges", "invoice allocation", "project split"], "aspire_invoice_allocation_access"],
        ["admin-tech-platform-vendor-bill-buffer", "Tech Platform Bill Buffer", "/admin/tech-platform-vendor-bill-buffer", ["tech platform", "vendor bill buffer", "xero bill import", "integration buffer"], "owner_or_switch_user"],
        ["admin-vendor-bill-processing", "Vendor Bill Processing", "/admin/vendor-bill-processing", ["manual bill", "bill upload", "aspire", "card spend", "vendor bill", "ap bill staging", "xero bill import", "invoice allocation"], "authenticated"],
        ["admin-email-management", "Email Template Management", "/admin/email-management", ["email templates"], "non_production"],
        ["admin-announcements", "Manage Announcements", "/admin/announcements", ["announcement settings"], "hr_or_marcom"],
        ["admin-knowledgebase", "Knowledgebase Settings", "/admin/knowledgebase", ["knowledgebase admin"], "knowledgebase_admin"],
        ["admin-qna", "Q&A Management", "/admin/qna", ["question bank"], "marcom"],
        ["admin-newsfeed", "Manage Newsfeed", "/admin/newsfeed", ["newsfeed settings", "shout outs", "company wins"], "hr_or_marcom"],
        ["admin-user-impersonator", "User Impersonator", "/admin/user-impersonator", ["switch user", "impersonate user"], "owner_or_switch_user"],
        ["admin-manage-account", "Manage Account", "/admin/manage-account", ["account settings"], "non_production"],
        ["admin-office", "Office Settings", "/admin/office", ["office admin"], "hr_access_all_office_or_management"],
        ["admin-department", "Department Settings", "/admin/department", ["department admin"], "hr_access_all_office_or_management"],
        ["admin-company", "Company Settings", "/admin/company", ["company admin"], "hr_access_all_office_or_management"],
        ["admin-email-banner", "Email Banners", "/admin/email-banner", ["banner settings"], "email_admin"],
        ["admin-signature-template", "Signature Templates", "/admin/email-signature-template", ["email signature"], "email_admin"],
        ["admin-ai-prompts", "AI Prompts", "/admin/ai-prompts", ["prompt management"], "email_admin"],
        ["admin-notifications", "Notification Management", "/admin/notifications", ["google chat", "notifications", "alerts", "escalation", "channels", "webhooks", "bot api"], "non_production"],
    ]),
    ...buildStaticScreens("HR", [
        ["hr-dashboard", "HR Dashboard", "/admin/hr/dashboard", ["people analytics", "hr analytics", "workforce dashboard"], "manage_hr"],
        ["hr-dashboard-pulse", "HR Pulse", "/admin/hr/dashboard?tab=pulse", ["employee pulse", "morale", "burnout", "workload", "engagement"], "manage_hr"],
        ["hr-dashboard-data-quality", "HR Data Quality", "/admin/hr/dashboard?tab=data-quality", ["employee data quality", "profile completeness", "missing dob", "missing nationality", "missing gender"], "manage_hr"],
        ["hr-recruitment", "Recruitment", "/admin/hr/recruitment", ["hiring", "vacancies", "talent pipeline"]],
        ["hr-appraisals", "HR Appraisals", "/admin/hr/appraisals", ["performance review admin"], "manage_hr"],
        ["hr-surveys", "HR Surveys", "/admin/hr/surveys", ["survey administration", "pulse survey", "enps admin"], "manage_hr"],
        ["hr-workplace-feedback", "Workplace Feedback", "/admin/hr/workplace-feedback", ["workplace feedback", "anonymous feedback", "feedback inbox", "hr feedback inbox"], "manage_hr"],
        ["hr-leaves-admin", "Leave Administration", "/admin/hr/leaves", ["leave admin", "holiday admin"], "hr_or_manager"],
        ["hr-assets", "Computers & Equipment", "/admin/hr/asset", ["assets", "equipment", "computer management"], "finance"],
        ["hr-trainings", "Training Administration", "/admin/hr/trainings", ["training admin", "course admin"]],
        ["hr-working-location", "MT Anywhere+ Administration", "/admin/hr/working-location", ["working location", "wfa admin"], "hr_or_manager"],
        ["hr-employees", "Employee Management", "/admin/hr/employees", ["employees", "user management"], "manager_or_finance"],
    ]),
    ...buildStaticScreens("Admin Finance", [
        ["admin-finance-mastercard-boost", "Mastercard Boost", "/admin/finances?tab=mastercard-boost", ["boost automation", "nuvei allocation", "mastercard fee"]],
        ["admin-finance-home", "Admin Finance", "/admin/finances", ["finance admin"]],
        ["admin-finance-expense-requests", "Admin Expense Requests", "/admin/finances?tab=expense-requests", ["expense approvals"]],
        ["admin-finance-xero", "Admin Xero", "/admin/finances?tab=xero", ["xero admin", "xero settings"]],
        ["admin-finance-cost-calculator", "Cost Calculator", "/admin/finances?tab=cost-calculator", ["master cost calculator", "cost calculator", "rates", "utilisation"]],
        ["admin-finance-reports", "Admin Finance Reports", "/admin/finances?tab=reports", ["finance reports"]],
        ["admin-finance-weekly-cashflow", "Weekly Cashflow Report", "/admin/finances?tab=weekly-cashflow", ["cashflow", "cash flow", "weekly cashflow", "weekly cash flow"]],
        ["admin-xero", "Xero Dashboard", "/admin/xero", ["xero sync", "xero admin"]],
    ]),
];

const TASK_SCREENS = buildStaticScreens("Tasks", [
    ["tasks-my-tasks", "My Tasks", "/tasks/my-tasks", ["assigned tasks", "task inbox"]],
    ["tasks-verification", "Verification Needed", "/tasks/verification", ["verify tasks", "approval queue"]],
    ["tasks-delegated", "Delegated Tasks", "/tasks/delegated", ["delegated work"]],
    ["tasks-overdue", "Overdue Tasks", "/tasks/overdue", ["late tasks"]],
    ["tasks-checklists", "Checklists", "/tasks/checklists", ["checklist library"]],
    ["tasks-templates", "Checklist Templates", "/tasks/templates", ["task templates"]],
    ["tasks-ai", "Task AI", "/tasks/ai", ["ai tasks", "task assistant"]],
    ["tasks-queues", "Queues", "/tasks/queues", ["queue inbox", "task queues"]],
    ["tasks-queue-management", "Queue Management", "/tasks/queue-management", ["queue setup", "task queue admin"]],
    ["tasks-dashboard", "Tasks Dashboard", "/tasks/dashboard", ["task metrics"]],
]);

const FINANCE_SCREENS = buildTabScreens({
    category: "Finances",
    basePath: "/finances",
    prefix: "finances",
    visibilityKey: "finance_access",
    tabs: {
        "sales-orders": { name: "Sales Orders", keywords: ["sales order", "so"] },
        "purchase-orders": { name: "Purchase Orders", keywords: ["purchase order", "po"] },
        "payment-terms": { name: "Payment Terms & Conditions", keywords: ["payment terms", "terms templates", "client terms"], visibilityKey: "finance" },
        "client-invoices": { name: "Client Invoices", keywords: ["invoices", "client billing"] },
        "invoice-follow-up": { name: "Invoice Follow-Up", keywords: ["draft invoice", "unpaid invoice", "invoice chase", "invoice follow up", "payment forecast"] },
        "vendor-bills": { name: "Vendor Bills", keywords: ["supplier bills", "ap bills"] },
        blotter: { name: "Blotter", keywords: ["cash blotter", "blotter report"], visibilityKey: "finance_report_access" },
        "agency-revenue": { name: "Agency Revenue", keywords: ["revenue summary"] },
        "credit-notes": { name: "Credit Notes", keywords: ["credit memo"] },
        "expense-requests": { name: "Expense Requests", keywords: ["expense claim", "reimbursement"] },
        "finance-tools": { name: "Finance Tools", keywords: ["finance utilities"] },
        "mc-passthrough": { name: "MC Passthrough", keywords: ["media commission passthrough"] },
        "projected-vendor-bills": { name: "Projected Vendor Bills", keywords: ["forecast vendor bills"] },
        "deferred-vendor-revenue": { name: "Deferred Vendor Revenue", keywords: ["deferred revenue"] },
        "boost-fees-discrepancy": { name: "Boost Fees Discrepancy", keywords: ["boost fee mismatch"] },
    },
});

const FINANCE_REPORT_SCREENS = buildViewScreens({
    category: "Finance Reports",
    basePath: "/finances?tab=reports",
    prefix: "finance-report",
    visibilityKey: "finance_report_access",
    views: {},
});

const PROJECT_MANAGEMENT_SCREENS = buildTabScreens({
    category: "Project Management",
    basePath: "/project-management",
    prefix: "pm",
    visibilityKey: "finance_access",
    tabs: {
        "my-projects": { name: "My Projects", keywords: ["assigned projects"] },
        projects: { name: "Projects", keywords: ["all projects"] },
        clients: { name: "Clients", keywords: ["client list"] },
        vendors: { name: "Vendors", keywords: ["supplier list"] },
        employees: { name: "Project Employees", keywords: ["project staff"] },
        classification: { name: "Classification", keywords: ["project types", "project classification"] },
        feedback: { name: "Feedback Templates", keywords: ["client feedback", "feedback forms"] },
        reports: { name: "Project Reports", keywords: ["pm reports", "project report hub"] },
    },
});

const PROJECT_REPORT_SCREENS = buildViewScreens({
    category: "Project Management Reports",
    basePath: "/project-management?tab=reports",
    prefix: "pm-report",
    visibilityKey: "finance_report_access",
    views: {
        "vendor-revenue": { name: "Vendor Revenue Recognition Report", keywords: ["vendor recognition", "vendor rev rec", "revenue recognition"] },
        "agency-revenue": { name: "Agency Revenue Recognition Report", keywords: ["agency revenue", "agency rev rec", "revenue recognition"] },
        "fy-revenue-analysis": { name: "FY Revenue Analysis Report", keywords: ["fiscal year revenue", "year revenue"] },
        "fy-cost-analysis": { name: "FY Cost Analysis Report", keywords: ["fiscal year cost", "year cost"] },
        "unallocated-invoices": { name: "Unallocated Invoices Report", keywords: ["invoice allocation"] },
        "unallocated-bills": { name: "Unallocated Vendor Bills Report", keywords: ["bill allocation", "unallocated bills"] },
        "xero-totals": { name: "Xero Totals Audit", keywords: ["xero totals"] },
        "invoice-integrity": { name: "Invoice Integrity Report", keywords: ["invoice check", "invoice audit"] },
        "vendor-costs-fy": { name: "Vendor Costs by FY Report", keywords: ["vendor costs", "fiscal year vendor cost"] },
        "av-revenue-fy": { name: "AV Revenue by FY Report", keywords: ["av revenue", "fiscal year av revenue"] },
        "project-finance": { name: "Project Finance Report", keywords: ["project financials"] },
        "lead-analysis": { name: "Lead Analysis Report", keywords: ["lead report", "pipeline analysis"] },
        "project-dashboard": { name: "Project Dashboard Report", keywords: ["dashboard report", "project summary"] },
        "quality-dashboard": { name: "Quality Dashboard", keywords: ["quality", "pic leaderboard", "issue tracking", "project quality"] },
        "cross-fy-movement": { name: "Cross-FY Revenue Movement Report", keywords: ["cross fy", "revenue movement"] },
        "revenue-adjustments": { name: "Revenue Adjustments Report", keywords: ["adjusted revenue"] },
        "archived-conversions": { name: "Archived Conversion Report", keywords: ["archived conversions"] },
        "pending-conversions": { name: "Pending Conversion Report", keywords: ["pending conversions"] },
        "deferred-by-invoice": { name: "Deferred Revenue by Invoice Report", keywords: ["deferred invoice"] },
        "bill-analysis": { name: "Bill Analysis Report", keywords: ["bill audit", "bill review"] },
        "payment-readiness": { name: "Payment Readiness Report", keywords: ["ready for payment", "vendor payment readiness"] },
        "vendor-fx-variance": { name: "Vendor FX Variance Report", keywords: ["foreign exchange variance", "fx variance"] },
        "xero-sync": { name: "Xero Sync Report", keywords: ["xero sync status"] },
        "exchange-rate-audit": { name: "Exchange Rate Audit Report", keywords: ["fx audit", "exchange rate check"] },
        "usd-conversion-audit": { name: "USD Conversion Audit Report", keywords: ["usd audit", "usd conversion"] },
        "allocation-audit": { name: "Allocation Audit Report", keywords: ["allocation check"] },
        "agency-revenue-department": { name: "Agency Revenue by Department Report", keywords: ["department revenue"] },
        "revenue-trends": { name: "Revenue Trends Report", keywords: ["revenue trend"] },
        "agency-revenue-by-project": { name: "Agency Revenue by Project Report", keywords: ["project revenue"] },
    },
});

const LEAVE_SCREENS = buildTabScreens({
    category: "Leaves",
    basePath: "/leaves",
    prefix: "leaves",
    tabs: {
        entitlements: { name: "Leave Entitlements", keywords: ["leave balance"] },
        "annual-leave": { name: "Leave Requests", keywords: ["leave request", "annual leave"] },
        "sick-leave": { name: "Sick Leave Records", keywords: ["medical leave"] },
        calendar: { name: "Leave Calendar", keywords: ["holiday calendar"] },
    },
});

const TIMESHEET_SCREENS = buildTabScreens({
    category: "Timesheet",
    basePath: "/timesheet",
    prefix: "timesheet",
    tabs: {
        "my-timesheet": { name: "My Timesheet", keywords: ["time entries"] },
        tasks: { name: "Timesheet Tasks", keywords: ["timesheet task view"] },
        "team-timesheets": { name: "Team Timesheets", keywords: ["team time tracking"] },
        "project-time": { name: "Project Time Report", keywords: ["time by project"] },
        "timesheet-report": { name: "Timesheet Report", keywords: ["time report"], visibilityKey: "timesheet_report_access" },
    },
});

const RECRUITMENT_SCREENS = buildTabScreens({
    category: "Recruitment",
    basePath: "/admin/hr/recruitment",
    prefix: "recruitment",
    tabs: {
        vacancies: { name: "Vacancies", keywords: ["job openings"] },
        "my-dashboard": { name: "Recruitment Dashboard", keywords: ["hiring dashboard"] },
        talents: { name: "Talents", keywords: ["candidates"] },
        evaluation: { name: "Evaluations", keywords: ["candidate scoring"] },
        "screening-questions": { name: "Screening Questions", keywords: ["interview questions"] },
        emails: { name: "Recruitment Emails", keywords: ["candidate emails"] },
        activity: { name: "Recruitment Activity", keywords: ["recruitment logs"] },
        setting: { name: "Recruitment Settings", keywords: ["hiring settings"] },
    },
});

const API_USAGE_SCREENS = buildStaticScreens("API Usage", [
    ["api-usage-ai", "AI Usage Analysis", "/admin/api-usage/ai-general", ["ai api usage"]],
    ["api-usage-currency", "Currency API Analysis", "/admin/api-usage/currency", ["fx api usage"]],
    ["api-usage-xero", "Xero API Analysis", "/admin/api-usage/xero", ["xero api usage"]],
], "owner_or_switch_user");

const TRAINING_TAB_SCREENS = buildStaticScreens("Training", [
    ["training-enrolled", "Enrolled Courses", "/training?tab=enrolled", ["my courses", "enrolled training"]],
    ["training-available", "Available Courses", "/training?tab=available", ["browse courses", "available training"]],
]);

export const SCREEN_REGISTRY = [
    ...STATIC_SCREENS,
    ...TASK_SCREENS,
    ...FINANCE_SCREENS,
    ...FINANCE_REPORT_SCREENS,
    ...PROJECT_MANAGEMENT_SCREENS,
    ...PROJECT_REPORT_SCREENS,
    ...LEAVE_SCREENS,
    ...TIMESHEET_SCREENS,
    ...RECRUITMENT_SCREENS,
    ...API_USAGE_SCREENS,
    ...TRAINING_TAB_SCREENS,
];

const visibilityChecks = {
    authenticated: () => true,
    admin_visible: () => roleService.isAdmin(),
    finance: () => roleService.hasRole(ROLES.ROLE_FINANCE),
    finance_access: () => roleService.canAccessFinancialData(),
    finance_report_access: () => roleService.hasRoleOrSpecialAccess(ROLES.ROLE_FINANCE, "finance_report"),
    manage_hr: () => roleService.canManageHR(),
    hr_or_manager: () => roleService.hasRole(ROLES.ROLE_HR) || roleService.isManager(),
    manager_or_finance: () => roleService.hasRole(ROLES.ROLE_FINANCE) || roleService.isManager(),
    marcom: () => roleService.hasRole(ROLES.ROLE_MARCOM),
    hr_or_marcom: () => roleService.hasRole(ROLES.ROLE_HR) || roleService.hasRole(ROLES.ROLE_MARCOM),
    knowledgebase_admin: () => roleService.hasRole(ROLES.ROLE_MARCOM) || Boolean(roleService.getCurrentUser()?.feature_document_management),
    owner_or_switch_user: () => roleService.hasRole(ROLES.ROLE_CAN_SWITCH_USER) || roleService.hasRole(ROLES.ROLE_OWNER),
    aspire_invoice_allocation_access: () => roleService.canAccessAspireInvoiceAllocation(),
    vendor_bill_processing_access: () => (
        roleService.canAccessAspireInvoiceAllocation() ||
        roleService.hasRole(ROLES.ROLE_TEAM_LEADER) ||
        roleService.hasRole(ROLES.ROLE_CAN_SWITCH_USER) ||
        roleService.hasRole(ROLES.ROLE_OWNER) ||
        roleService.hasRole(ROLES.ROLE_SUPER_ADMIN)
    ),
    email_admin: () =>
        (roleService.hasRole(ROLES.ROLE_HR) && roleService.hasRole(ROLES.ROLE_ACCESS_ALL_OFFICE)) ||
        roleService.hasRole(ROLES.ROLE_MARCOM),
    hr_access_all_office_or_management: () =>
        (roleService.hasRole(ROLES.ROLE_HR) && roleService.hasRole(ROLES.ROLE_ACCESS_ALL_OFFICE)) ||
        roleService.hasRole(ROLES.ROLE_MANAGEMENT) ||
        roleService.hasRole(ROLES.ROLE_OWNER),
    non_production: () => process.env.VITE_ENV !== "production",
    timesheet_report_access: () =>
        roleService.hasRole(ROLES.ROLE_FINANCE) ||
        roleService.hasSpecialAccess("project_timesheet_report"),
};

export const isScreenVisible = (screen) => {
    const checker = visibilityChecks[screen.visibilityKey] || visibilityChecks.authenticated;
    return checker();
};

export const getVisibleScreens = () => SCREEN_REGISTRY.filter(isScreenVisible);

const scoreScreen = (screen, normalizedQuery, tokens) => {
    const normalizedName = normalizeText(screen.name);
    const normalizedCategory = normalizeText(screen.category);
    const haystacks = [normalizedName, normalizedCategory, normalizeText(screen.path), ...screen.keywords];

    const matchesAllTokens = tokens.every((token) => haystacks.some((value) => value.includes(token)));
    if (!matchesAllTokens) {
        return -1;
    }

    let score = 0;

    if (normalizedName === normalizedQuery) {
        score += 1200;
    }
    if (screen.keywords.includes(normalizedQuery)) {
        score += 900;
    }
    if (normalizedName.startsWith(normalizedQuery)) {
        score += 500;
    }
    if (screen.path.includes(normalizedQuery.replace(/\s+/g, "-"))) {
        score += 120;
    }

    tokens.forEach((token) => {
        if (normalizedName.startsWith(token)) score += 120;
        if (normalizedName.includes(token)) score += 70;
        if (screen.keywords.some((keyword) => keyword.startsWith(token))) score += 55;
        if (screen.keywords.some((keyword) => keyword.includes(token))) score += 35;
        if (normalizedCategory.includes(token)) score += 15;
    });

    return score;
};

export const searchScreens = (query, screens = getVisibleScreens()) => {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) {
        return screens.slice(0, 12);
    }

    const tokens = normalizedQuery.split(" ").filter(Boolean);

    return screens
        .map((screen) => ({ screen, score: scoreScreen(screen, normalizedQuery, tokens) }))
        .filter((item) => item.score >= 0)
        .sort((a, b) => {
            if (b.score !== a.score) return b.score - a.score;
            return a.screen.name.localeCompare(b.screen.name);
        })
        .map((item) => item.screen)
        .slice(0, 12);
};

export const getScreenByPath = (path, screens = getVisibleScreens()) =>
    screens.find((screen) => screen.path === path) || null;

export const getRegistryPaths = () => SCREEN_REGISTRY.map((screen) => screen.path);

// Dynamic screen patterns for entity detail pages not in the static registry
const DYNAMIC_SCREEN_PATTERNS = [
    { pattern: /^\/project-management\/project\/(\d+)$/, category: "Project Management", defaultName: (id) => `Project #${id}` },
    { pattern: /^\/project-management\/client\/(\d+)$/, category: "Project Management", defaultName: (id) => `Client #${id}` },
    { pattern: /^\/project-management\/vendor\/(\d+)$/, category: "Project Management", defaultName: (id) => `Vendor #${id}` },
    { pattern: /^\/project-management\/contact\/(\d+)$/, category: "Project Management", defaultName: (id) => `Contact #${id}` },
    { pattern: /^\/project-management\/pic\/(\d+)$/, category: "Project Management", defaultName: (id) => `PIC #${id}` },
    { pattern: /^\/wiki\/(\d+)/, category: "Knowledge & AI", defaultName: (id) => `Wiki Page #${id}` },
    { pattern: /^\/finances\/sales-order\/(\d+)$/, category: "Finances", defaultName: (id) => `Sales Order #${id}` },
    { pattern: /^\/finances\/invoice\/(.+)$/, category: "Finances", defaultName: (id) => `Invoice ${decodeURIComponent(id)}` },
    { pattern: /^\/finances\/vendor-bills\/(\d+)$/, category: "Finances", defaultName: (id) => `Vendor Bill #${id}` },
];

export const matchDynamicScreen = (path) => {
    for (const { pattern, category, defaultName } of DYNAMIC_SCREEN_PATTERNS) {
        const match = path.match(pattern);
        if (match) {
            return {
                id: `dynamic-${path}`,
                name: defaultName(match[1]),
                path,
                category,
                keywords: [],
            };
        }
    }
    return null;
};
